import { useGate, useStore, useStoreMap } from 'effector-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { roadAccidentModel } from '~/entities/RoadAccident'
import { AddRepairOrder } from '~/features/RepairOrders/AddRepairOrder'
import { editRoadAccidentModel } from '~/features/RoadAccidents/EditRoadAccident'
import { Gate } from '~/features/RoadAccidents/EditRoadAccident/model'
import { CarWithRelationOption } from '~/shared/api'
import { Option } from '~/shared/config/constants'
import { formatDateTimeForUI } from '~/shared/lib/date'
import { Breadcrumbs, BreadcrumbsList } from '~/shared/ui/Breadcrumbs'
import { Container, PageContainer, PageTitle } from '~/shared/ui/Layout'
import { NotFound } from '~/shared/ui/NotFound'
import {
  QuickNavigation,
  QuickNavigationOptions,
} from '~/shared/ui/QuickNavigation'

const quickNavigationOptions: QuickNavigationOptions = [
  { labelI18n: 'form.car', anchor: 'car' },
  { labelI18n: 'form.description repair', anchor: 'descriptionRepair' },
  { labelI18n: 'form.sto', anchor: 'sto' },
  { labelI18n: 'form.cost', anchor: 'cost' },
  { labelI18n: 'form.files', anchor: 'files' },
]

export const RoadAccidentAddRepairOrderPage = () => {
  const { t } = useTranslation()
  const params = useParams<{ roadAccidentId: string }>()
  const roadAccidentId = params.roadAccidentId as string
  useGate(Gate, { roadAccidentId })

  const isLoading = useStore(roadAccidentModel.requestFx.pending)
  const roadAccident = useStore(editRoadAccidentModel.$roadAccident)

  const number = roadAccident?.getNumber()
  const createdAt = roadAccident?.getCreatedAt()

  const title = `${t('road accident')} № ${number || '...'} ${t('from')} ${
    createdAt ? formatDateTimeForUI(createdAt) : ''
  }`

  const breadcrumbs: BreadcrumbsList = useMemo(
    () => [
      { titleI18n: 'road accidents', link: '/road-accidents' },
      {
        title: title,
        link: `/road-accidents/${roadAccidentId}`,
      },
      { titleI18n: 'create repair order' },
    ],
    [roadAccidentId, title],
  )

  const defaultValues = useStoreMap(
    editRoadAccidentModel.$roadAccident,
    (roadAccident) => {
      const car = roadAccident?.getCar()
      const inspection = roadAccident?.getInspection()
      const mileage = roadAccident?.getMileage()

      return {
        mileage,
        carOption:
          car && inspection
            ? ({
                id: car?.getApiId(),
                label: inspection?.getPlateNumber(),
                subdivision: roadAccident?.getSubdivision()?.getTitle(),
                vin: car?.getVin(),
                brandModel: car?.getBrandModel()?.getTitle(),
              } as CarWithRelationOption)
            : null,
        roadAccidentIds: roadAccident
          ? [
              {
                id: roadAccident.getApiId(),
                label: roadAccident?.getNumber(),
              } as Option,
            ]
          : null,
      }
    },
  )

  const error = roadAccidentModel.useRoadAccidentError(roadAccidentId as string)
  if (!roadAccidentId || error) {
    return <NotFound message={t('alert.road accident load error')} />
  }

  return (
    <PageContainer title={t('create repair order')}>
      <Container isHeader>
        <Breadcrumbs list={breadcrumbs} lastLoading={isLoading} />
        <PageTitle>{t('create repair order')}</PageTitle>
      </Container>

      <AddRepairOrder isLoading={isLoading} defaultValues={defaultValues} />

      <QuickNavigation options={quickNavigationOptions} />
    </PageContainer>
  )
}
